// Mensuration Knowledge Graph Samples
// Organized by difficulty levels with systematic naming

export const mensurationSamples = {
  fundamental: [
    {
      id: 'mensuration_fund_01',
      problem: "Find the area of a rectangle with length 8 cm and width 5 cm",
      problemType: "mensuration",
      difficulty: "fundamental",
      description: "Basic rectangle area calculation",
      graphData: {
        nodes: [
          {
            id: "length",
            name: "Length",
            type: "concepts",
            description: "The length of the rectangle",
            value: "8 cm",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "width",
            name: "Width",
            type: "concepts",
            description: "The width of the rectangle",
            value: "5 cm",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "area_formula",
            name: "Area Formula",
            type: "formulas",
            description: "Area = length × width",
            value: "Area = 8 × 5",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "calculation",
            name: "Calculation",
            type: "values",
            description: "Calculate the area",
            value: "Area = 40",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The area of the rectangle",
            value: "40 cm²",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "rectangle_area_formula",
            name: "Rectangle Area Formula",
            type: "formulas",
            description: "Area of rectangle = length × width",
            value: "Area = length × width",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "identify_dimensions",
            name: "Identify Dimensions",
            type: "steps",
            description: "Recognize the given dimensions",
            value: "Step 1: length = 8 cm, width = 5 cm",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "apply_formula",
            name: "Apply Formula",
            type: "steps",
            description: "Use the rectangle area formula",
            value: "Step 2: Area = 8 × 5",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "calculate_area",
            name: "Calculate Area",
            type: "steps",
            description: "Perform the multiplication",
            value: "Step 3: Area = 40",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "write_answer",
            name: "Write Answer",
            type: "steps",
            description: "State the final answer with units",
            value: "Step 4: Area = 40 cm²",
            importance: 10,
            x: 0, y: 150, z: 20
          }
        ],
        links: [
          { from: "length", to: "area_formula", type: "used in", strength: 9 },
          { from: "width", to: "area_formula", type: "used in", strength: 9 },
          { from: "area_formula", to: "calculation", type: "gives", strength: 10 },
          { from: "calculation", to: "result", type: "equals", strength: 10 },
          { from: "rectangle_area_formula", to: "result", type: "gives", strength: 10 },
          { from: "identify_dimensions", to: "apply_formula", type: "enables", strength: 8 },
          { from: "apply_formula", to: "calculate_area", type: "enables", strength: 9 },
          { from: "calculate_area", to: "write_answer", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'mensuration_fund_02',
      problem: "Find the area of a triangle with base 6 cm and height 4 cm",
      problemType: "mensuration",
      difficulty: "fundamental",
      description: "Basic triangle area calculation",
      graphData: {
        nodes: [
          {
            id: "base",
            name: "Base",
            type: "concepts",
            description: "The base of the triangle",
            value: "6 cm",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "height",
            name: "Height",
            type: "concepts",
            description: "The height of the triangle",
            value: "4 cm",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "triangle_area_formula",
            name: "Triangle Area Formula",
            type: "formulas",
            description: "Area of triangle = ½ × base × height",
            value: "Area = ½ × base × height",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "area_formula",
            name: "Area Formula",
            type: "formulas",
            description: "Area = ½ × base × height",
            value: "Area = ½ × 6 × 4",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "calculation",
            name: "Calculation",
            type: "values",
            description: "Calculate the area",
            value: "Area = 12",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The area of the triangle",
            value: "12 cm²",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "step1_identify",
            name: "Step 1: Identify",
            type: "steps",
            description: "Recognize the given dimensions",
            value: "Step 1: base = 6 cm, height = 4 cm",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "step2_apply_formula",
            name: "Step 2: Apply Formula",
            type: "steps",
            description: "Use the triangle area formula",
            value: "Step 2: Area = ½ × 6 × 4",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "step3_calculate",
            name: "Step 3: Calculate",
            type: "steps",
            description: "Perform the calculation",
            value: "Step 3: Area = 12",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "step4_write_answer",
            name: "Step 4: Write Answer",
            type: "steps",
            description: "State the final answer with units",
            value: "Step 4: Area = 12 cm²",
            importance: 10,
            x: 0, y: 150, z: 20
          }
        ],
        links: [
          { from: "base", to: "area_formula", type: "used in", strength: 9 },
          { from: "height", to: "area_formula", type: "used in", strength: 9 },
          { from: "area_formula", to: "calculation", type: "gives", strength: 10 },
          { from: "calculation", to: "result", type: "equals", strength: 10 },
          { from: "triangle_area_formula", to: "result", type: "gives", strength: 10 },
          { from: "step1_identify", to: "step2_apply_formula", type: "enables", strength: 8 },
          { from: "step2_apply_formula", to: "step3_calculate", type: "enables", strength: 9 },
          { from: "step3_calculate", to: "step4_write_answer", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'mensuration_fund_03',
      problem: "Find the area of a circle with radius 3 cm",
      problemType: "mensuration",
      difficulty: "fundamental",
      description: "Basic circle area calculation",
      graphData: {
        nodes: [
          {
            id: "radius",
            name: "Radius",
            type: "concepts",
            description: "The radius of the circle",
            value: "3 cm",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "circle_area_formula",
            name: "Circle Area Formula",
            type: "formulas",
            description: "Area of circle = πr²",
            value: "Area = πr²",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "area_formula",
            name: "Area Formula",
            type: "formulas",
            description: "Area = πr²",
            value: "Area = π × 3²",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "calculation",
            name: "Calculation",
            type: "values",
            description: "Calculate the area",
            value: "Area = π × 9 = 28.27",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The area of the circle",
            value: "28.27 cm²",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "step1_identify",
            name: "Step 1: Identify",
            type: "steps",
            description: "Recognize the given radius",
            value: "Step 1: radius = 3 cm",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "step2_apply_formula",
            name: "Step 2: Apply Formula",
            type: "steps",
            description: "Use the circle area formula",
            value: "Step 2: Area = π × 3²",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "step3_calculate",
            name: "Step 3: Calculate",
            type: "steps",
            description: "Perform the calculation",
            value: "Step 3: Area = π × 9 = 28.27",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "step4_write_answer",
            name: "Step 4: Write Answer",
            type: "steps",
            description: "State the final answer with units",
            value: "Step 4: Area = 28.27 cm²",
            importance: 10,
            x: 0, y: 150, z: 20
          }
        ],
        links: [
          { from: "radius", to: "area_formula", type: "used in", strength: 9 },
          { from: "area_formula", to: "calculation", type: "gives", strength: 10 },
          { from: "calculation", to: "result", type: "equals", strength: 10 },
          { from: "circle_area_formula", to: "result", type: "gives", strength: 10 },
          { from: "step1_identify", to: "step2_apply_formula", type: "enables", strength: 8 },
          { from: "step2_apply_formula", to: "step3_calculate", type: "enables", strength: 9 },
          { from: "step3_calculate", to: "step4_write_answer", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'mensuration_fund_04',
      problem: "Find the volume of a cube with side length 4 cm",
      problemType: "mensuration",
      difficulty: "fundamental",
      description: "Basic cube volume calculation",
      graphData: {
        nodes: [
          {
            id: "side_length",
            name: "Side Length",
            type: "concepts",
            description: "The length of each side",
            value: "4 cm",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "cube_volume_formula",
            name: "Cube Volume Formula",
            type: "formulas",
            description: "Volume of cube = side³",
            value: "Volume = side³",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "volume_formula",
            name: "Volume Formula",
            type: "formulas",
            description: "Volume = side³",
            value: "Volume = 4³",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "calculation",
            name: "Calculation",
            type: "values",
            description: "Calculate the volume",
            value: "Volume = 64",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The volume of the cube",
            value: "64 cm³",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "step1_identify",
            name: "Step 1: Identify",
            type: "steps",
            description: "Recognize the given side length",
            value: "Step 1: side length = 4 cm",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "step2_apply_formula",
            name: "Step 2: Apply Formula",
            type: "steps",
            description: "Use the cube volume formula",
            value: "Step 2: Volume = 4³",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "step3_calculate",
            name: "Step 3: Calculate",
            type: "steps",
            description: "Perform the calculation",
            value: "Step 3: Volume = 64",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "step4_write_answer",
            name: "Step 4: Write Answer",
            type: "steps",
            description: "State the final answer with units",
            value: "Step 4: Volume = 64 cm³",
            importance: 10,
            x: 0, y: 150, z: 20
          }
        ],
        links: [
          { from: "side_length", to: "volume_formula", type: "used in", strength: 9 },
          { from: "volume_formula", to: "calculation", type: "gives", strength: 10 },
          { from: "calculation", to: "result", type: "equals", strength: 10 },
          { from: "cube_volume_formula", to: "result", type: "gives", strength: 10 },
          { from: "step1_identify", to: "step2_apply_formula", type: "enables", strength: 8 },
          { from: "step2_apply_formula", to: "step3_calculate", type: "enables", strength: 9 },
          { from: "step3_calculate", to: "step4_write_answer", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'mensuration_fund_05',
      problem: "Find the volume of a cylinder with radius 2 cm and height 5 cm",
      problemType: "mensuration",
      difficulty: "fundamental",
      description: "Basic cylinder volume calculation",
      graphData: {
        nodes: [
          {
            id: "radius",
            name: "Radius",
            type: "concepts",
            description: "The radius of the cylinder",
            value: "2 cm",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "height",
            name: "Height",
            type: "concepts",
            description: "The height of the cylinder",
            value: "5 cm",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "cylinder_volume_formula",
            name: "Cylinder Volume Formula",
            type: "formulas",
            description: "Volume of cylinder = πr²h",
            value: "Volume = πr²h",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "volume_formula",
            name: "Volume Formula",
            type: "formulas",
            description: "Volume = πr²h",
            value: "Volume = π × 2² × 5",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "calculation",
            name: "Calculation",
            type: "values",
            description: "Calculate the volume",
            value: "Volume = π × 4 × 5 = 62.83",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The volume of the cylinder",
            value: "62.83 cm³",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "step1_identify",
            name: "Step 1: Identify",
            type: "steps",
            description: "Recognize the given dimensions",
            value: "Step 1: radius = 2 cm, height = 5 cm",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "step2_apply_formula",
            name: "Step 2: Apply Formula",
            type: "steps",
            description: "Use the cylinder volume formula",
            value: "Step 2: Volume = π × 2² × 5",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "step3_calculate",
            name: "Step 3: Calculate",
            type: "steps",
            description: "Perform the calculation",
            value: "Step 3: Volume = π × 4 × 5 = 62.83",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "step4_write_answer",
            name: "Step 4: Write Answer",
            type: "steps",
            description: "State the final answer with units",
            value: "Step 4: Volume = 62.83 cm³",
            importance: 10,
            x: 0, y: 150, z: 20
          }
        ],
        links: [
          { from: "radius", to: "volume_formula", type: "used in", strength: 9 },
          { from: "height", to: "volume_formula", type: "used in", strength: 9 },
          { from: "volume_formula", to: "calculation", type: "gives", strength: 10 },
          { from: "calculation", to: "result", type: "equals", strength: 10 },
          { from: "cylinder_volume_formula", to: "result", type: "gives", strength: 10 },
          { from: "step1_identify", to: "step2_apply_formula", type: "enables", strength: 8 },
          { from: "step2_apply_formula", to: "step3_calculate", type: "enables", strength: 9 },
          { from: "step3_calculate", to: "step4_write_answer", type: "equals", strength: 10 }
        ]
      }
    }
  ],
  
  intermediate: [
    {
      id: 'mensuration_inter_01',
      problem: "Find the area of a trapezium with parallel sides 6 cm and 10 cm, and height 4 cm",
      problemType: "mensuration",
      difficulty: "intermediate",
      description: "Trapezium area calculation",
      graphData: {
        nodes: [
          {
            id: "parallel_side1",
            name: "First Parallel Side",
            type: "concepts",
            description: "First parallel side",
            value: "6 cm",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "parallel_side2",
            name: "Second Parallel Side",
            type: "concepts",
            description: "Second parallel side",
            value: "10 cm",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "height",
            name: "Height",
            type: "concepts",
            description: "Height of trapezium",
            value: "4 cm",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "trapezium_area_formula",
            name: "Trapezium Area Formula",
            type: "formulas",
            description: "Area of trapezium = ½(a + b)h",
            value: "Area = ½(a + b)h",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "area_formula",
            name: "Area Formula",
            type: "formulas",
            description: "Area = ½(a + b)h",
            value: "Area = ½(6 + 10)4",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "calculation",
            name: "Calculation",
            type: "values",
            description: "Calculate the area",
            value: "Area = ½(16)4 = 32",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The area of the trapezium",
            value: "32 cm²",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "step1_identify",
            name: "Step 1: Identify",
            type: "steps",
            description: "Recognize the given dimensions",
            value: "Step 1: a = 6 cm, b = 10 cm, h = 4 cm",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "step2_apply_formula",
            name: "Step 2: Apply Formula",
            type: "steps",
            description: "Use the trapezium area formula",
            value: "Step 2: Area = ½(6 + 10)4",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "step3_calculate",
            name: "Step 3: Calculate",
            type: "steps",
            description: "Perform the calculation",
            value: "Step 3: Area = ½(16)4 = 32",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "step4_write_answer",
            name: "Step 4: Write Answer",
            type: "steps",
            description: "State the final answer with units",
            value: "Step 4: Area = 32 cm²",
            importance: 10,
            x: 0, y: 150, z: 20
          }
        ],
        links: [
          { from: "parallel_side1", to: "area_formula", type: "used in", strength: 9 },
          { from: "parallel_side2", to: "area_formula", type: "used in", strength: 9 },
          { from: "height", to: "area_formula", type: "used in", strength: 9 },
          { from: "area_formula", to: "calculation", type: "gives", strength: 10 },
          { from: "calculation", to: "result", type: "equals", strength: 10 },
          { from: "trapezium_area_formula", to: "result", type: "gives", strength: 10 },
          { from: "step1_identify", to: "step2_apply_formula", type: "enables", strength: 8 },
          { from: "step2_apply_formula", to: "step3_calculate", type: "enables", strength: 9 },
          { from: "step3_calculate", to: "step4_write_answer", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'mensuration_inter_02',
      problem: "Find the surface area of a sphere with radius 3 cm",
      problemType: "mensuration",
      difficulty: "intermediate",
      description: "Sphere surface area calculation",
      graphData: {
        nodes: [
          {
            id: "radius",
            name: "Radius",
            type: "concepts",
            description: "The radius of the sphere",
            value: "3 cm",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "sphere_surface_area_formula",
            name: "Sphere Surface Area Formula",
            type: "formulas",
            description: "Surface area of sphere = 4πr²",
            value: "Surface Area = 4πr²",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "surface_area_formula",
            name: "Surface Area Formula",
            type: "formulas",
            description: "Surface Area = 4πr²",
            value: "Surface Area = 4π × 3²",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "calculation",
            name: "Calculation",
            type: "values",
            description: "Calculate the surface area",
            value: "Surface Area = 4π × 9 = 113.1",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The surface area of the sphere",
            value: "113.1 cm²",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "step1_identify",
            name: "Step 1: Identify",
            type: "steps",
            description: "Recognize the given radius",
            value: "Step 1: radius = 3 cm",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "step2_apply_formula",
            name: "Step 2: Apply Formula",
            type: "steps",
            description: "Use the sphere surface area formula",
            value: "Step 2: Surface Area = 4π × 3²",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "step3_calculate",
            name: "Step 3: Calculate",
            type: "steps",
            description: "Perform the calculation",
            value: "Step 3: Surface Area = 4π × 9 = 113.1",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "step4_write_answer",
            name: "Step 4: Write Answer",
            type: "steps",
            description: "State the final answer with units",
            value: "Step 4: Surface Area = 113.1 cm²",
            importance: 10,
            x: 0, y: 150, z: 20
          }
        ],
        links: [
          { from: "radius", to: "surface_area_formula", type: "used in", strength: 9 },
          { from: "surface_area_formula", to: "calculation", type: "gives", strength: 10 },
          { from: "calculation", to: "result", type: "equals", strength: 10 },
          { from: "sphere_surface_area_formula", to: "result", type: "gives", strength: 10 },
          { from: "step1_identify", to: "step2_apply_formula", type: "enables", strength: 8 },
          { from: "step2_apply_formula", to: "step3_calculate", type: "enables", strength: 9 },
          { from: "step3_calculate", to: "step4_write_answer", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'mensuration_inter_03',
      problem: "Find the volume of a cone with radius 2 cm and height 6 cm",
      problemType: "mensuration",
      difficulty: "intermediate",
      description: "Cone volume calculation",
      graphData: {
        nodes: [
          {
            id: "radius",
            name: "Radius",
            type: "concepts",
            description: "The radius of the cone",
            value: "2 cm",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "height",
            name: "Height",
            type: "concepts",
            description: "The height of the cone",
            value: "6 cm",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "cone_volume_formula",
            name: "Cone Volume Formula",
            type: "formulas",
            description: "Volume of cone = ⅓πr²h",
            value: "Volume = ⅓πr²h",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "volume_formula",
            name: "Volume Formula",
            type: "formulas",
            description: "Volume = ⅓πr²h",
            value: "Volume = ⅓π × 2² × 6",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "calculation",
            name: "Calculation",
            type: "values",
            description: "Calculate the volume",
            value: "Volume = ⅓π × 4 × 6 = 25.13",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The volume of the cone",
            value: "25.13 cm³",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "step1_identify",
            name: "Step 1: Identify",
            type: "steps",
            description: "Recognize the given dimensions",
            value: "Step 1: radius = 2 cm, height = 6 cm",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "step2_apply_formula",
            name: "Step 2: Apply Formula",
            type: "steps",
            description: "Use the cone volume formula",
            value: "Step 2: Volume = ⅓π × 2² × 6",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "step3_calculate",
            name: "Step 3: Calculate",
            type: "steps",
            description: "Perform the calculation",
            value: "Step 3: Volume = ⅓π × 4 × 6 = 25.13",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "step4_write_answer",
            name: "Step 4: Write Answer",
            type: "steps",
            description: "State the final answer with units",
            value: "Step 4: Volume = 25.13 cm³",
            importance: 10,
            x: 0, y: 150, z: 20
          }
        ],
        links: [
          { from: "radius", to: "volume_formula", type: "used in", strength: 9 },
          { from: "height", to: "volume_formula", type: "used in", strength: 9 },
          { from: "volume_formula", to: "calculation", type: "gives", strength: 10 },
          { from: "calculation", to: "result", type: "equals", strength: 10 },
          { from: "cone_volume_formula", to: "result", type: "gives", strength: 10 },
          { from: "step1_identify", to: "step2_apply_formula", type: "enables", strength: 8 },
          { from: "step2_apply_formula", to: "step3_calculate", type: "enables", strength: 9 },
          { from: "step3_calculate", to: "step4_write_answer", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'mensuration_inter_04',
      problem: "Find the area of a sector of a circle with radius 5 cm and central angle 60°",
      problemType: "mensuration",
      difficulty: "intermediate",
      description: "Circle sector area calculation",
      graphData: {
        nodes: [
          {
            id: "radius",
            name: "Radius",
            type: "concepts",
            description: "The radius of the circle",
            value: "5 cm",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "central_angle",
            name: "Central Angle",
            type: "concepts",
            description: "The central angle in degrees",
            value: "60°",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "sector_area_formula_general",
            name: "Sector Area Formula",
            type: "formulas",
            description: "Area of sector = (θ/360) × πr²",
            value: "Area = (θ/360) × πr²",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "sector_area_formula",
            name: "Sector Area Formula",
            type: "formulas",
            description: "Area = (θ/360) × πr²",
            value: "Area = (60/360) × π × 5²",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "calculation",
            name: "Calculation",
            type: "values",
            description: "Calculate the area",
            value: "Area = (1/6) × π × 25 = 13.09",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The area of the sector",
            value: "13.09 cm²",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "step1_identify",
            name: "Step 1: Identify",
            type: "steps",
            description: "Recognize the given dimensions",
            value: "Step 1: radius = 5 cm, central angle = 60°",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "step2_apply_formula",
            name: "Step 2: Apply Formula",
            type: "steps",
            description: "Use the sector area formula",
            value: "Step 2: Area = (60/360) × π × 5²",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "step3_calculate",
            name: "Step 3: Calculate",
            type: "steps",
            description: "Perform the calculation",
            value: "Step 3: Area = (1/6) × π × 25 = 13.09",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "step4_write_answer",
            name: "Step 4: Write Answer",
            type: "steps",
            description: "State the final answer with units",
            value: "Step 4: Area = 13.09 cm²",
            importance: 10,
            x: 0, y: 150, z: 20
          }
        ],
        links: [
          { from: "radius", to: "sector_area_formula", type: "used in", strength: 9 },
          { from: "central_angle", to: "sector_area_formula", type: "used in", strength: 9 },
          { from: "sector_area_formula", to: "calculation", type: "gives", strength: 10 },
          { from: "calculation", to: "result", type: "equals", strength: 10 },
          { from: "sector_area_formula_general", to: "result", type: "gives", strength: 10 },
          { from: "step1_identify", to: "step2_apply_formula", type: "enables", strength: 8 },
          { from: "step2_apply_formula", to: "step3_calculate", type: "enables", strength: 9 },
          { from: "step3_calculate", to: "step4_write_answer", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'mensuration_inter_05',
      problem: "Find the volume of a rectangular prism with dimensions 3 cm × 4 cm × 5 cm",
      problemType: "mensuration",
      difficulty: "intermediate",
      description: "Rectangular prism volume calculation",
      graphData: {
        nodes: [
          {
            id: "length",
            name: "Length",
            type: "concepts",
            description: "The length of the prism",
            value: "3 cm",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "width",
            name: "Width",
            type: "concepts",
            description: "The width of the prism",
            value: "4 cm",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "height",
            name: "Height",
            type: "concepts",
            description: "The height of the prism",
            value: "5 cm",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "rectangular_prism_volume_formula",
            name: "Rectangular Prism Volume Formula",
            type: "formulas",
            description: "Volume of rectangular prism = length × width × height",
            value: "Volume = length × width × height",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "volume_formula",
            name: "Volume Formula",
            type: "formulas",
            description: "Volume = length × width × height",
            value: "Volume = 3 × 4 × 5",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "calculation",
            name: "Calculation",
            type: "values",
            description: "Calculate the volume",
            value: "Volume = 60",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The volume of the prism",
            value: "60 cm³",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "step1_identify",
            name: "Step 1: Identify",
            type: "steps",
            description: "Recognize the given dimensions",
            value: "Step 1: length = 3 cm, width = 4 cm, height = 5 cm",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "step2_apply_formula",
            name: "Step 2: Apply Formula",
            type: "steps",
            description: "Use the rectangular prism volume formula",
            value: "Step 2: Volume = 3 × 4 × 5",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "step3_calculate",
            name: "Step 3: Calculate",
            type: "steps",
            description: "Perform the calculation",
            value: "Step 3: Volume = 60",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "step4_write_answer",
            name: "Step 4: Write Answer",
            type: "steps",
            description: "State the final answer with units",
            value: "Step 4: Volume = 60 cm³",
            importance: 10,
            x: 0, y: 150, z: 20
          }
        ],
        links: [
          { from: "length", to: "volume_formula", type: "used in", strength: 9 },
          { from: "width", to: "volume_formula", type: "used in", strength: 9 },
          { from: "height", to: "volume_formula", type: "used in", strength: 9 },
          { from: "volume_formula", to: "calculation", type: "gives", strength: 10 },
          { from: "calculation", to: "result", type: "equals", strength: 10 },
          { from: "rectangular_prism_volume_formula", to: "result", type: "gives", strength: 10 },
          { from: "step1_identify", to: "step2_apply_formula", type: "enables", strength: 8 },
          { from: "step2_apply_formula", to: "step3_calculate", type: "enables", strength: 9 },
          { from: "step3_calculate", to: "step4_write_answer", type: "equals", strength: 10 }
        ]
      }
    }
  ],
  
  harder: [
    {
      id: 'mensuration_hard_01',
      problem: "Find the area of a regular hexagon with side length 4 cm",
      problemType: "mensuration",
      difficulty: "harder",
      description: "Regular hexagon area calculation",
      graphData: {
        nodes: [
          {
            id: "side_length",
            name: "Side Length",
            type: "concepts",
            description: "The length of each side",
            value: "4 cm",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "apothem",
            name: "Apothem",
            type: "values",
            description: "Calculate apothem",
            value: "a = s√3/2 = 2√3 cm",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "perimeter",
            name: "Perimeter",
            type: "values",
            description: "Calculate perimeter",
            value: "P = 6s = 24 cm",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "hexagon_area_formula",
            name: "Regular Hexagon Area Formula",
            type: "formulas",
            description: "Area of regular hexagon = ½ × perimeter × apothem",
            value: "Area = ½ × perimeter × apothem",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "area_formula",
            name: "Area Formula",
            type: "formulas",
            description: "Area = ½ × perimeter × apothem",
            value: "Area = ½ × 24 × 2√3",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "calculation",
            name: "Calculation",
            type: "values",
            description: "Calculate the area",
            value: "Area = 24√3 = 41.57",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The area of the hexagon",
            value: "41.57 cm²",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "step1_identify",
            name: "Step 1: Identify",
            type: "steps",
            description: "Recognize the given side length",
            value: "Step 1: side length = 4 cm",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "step2_calculate_apothem",
            name: "Step 2: Calculate Apothem",
            type: "steps",
            description: "Calculate the apothem using formula",
            value: "Step 2: apothem = s√3/2 = 2√3 cm",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "step3_calculate_perimeter",
            name: "Step 3: Calculate Perimeter",
            type: "steps",
            description: "Calculate the perimeter",
            value: "Step 3: perimeter = 6s = 24 cm",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "step4_apply_formula",
            name: "Step 4: Apply Formula",
            type: "steps",
            description: "Use the hexagon area formula",
            value: "Step 4: Area = ½ × 24 × 2√3",
            importance: 10,
            x: 0, y: 150, z: 20
          },
          {
            id: "step5_calculate",
            name: "Step 5: Calculate",
            type: "steps",
            description: "Perform the final calculation",
            value: "Step 5: Area = 24√3 = 41.57",
            importance: 10,
            x: 0, y: 200, z: 20
          },
          {
            id: "step6_write_answer",
            name: "Step 6: Write Answer",
            type: "steps",
            description: "State the final answer with units",
            value: "Step 6: Area = 41.57 cm²",
            importance: 10,
            x: 0, y: 250, z: 20
          }
        ],
        links: [
          { from: "side_length", to: "apothem", type: "used to calculate", strength: 9 },
          { from: "side_length", to: "perimeter", type: "used to calculate", strength: 9 },
          { from: "apothem", to: "area_formula", type: "used in", strength: 9 },
          { from: "perimeter", to: "area_formula", type: "used in", strength: 9 },
          { from: "area_formula", to: "calculation", type: "gives", strength: 10 },
          { from: "calculation", to: "result", type: "equals", strength: 10 },
          { from: "hexagon_area_formula", to: "result", type: "gives", strength: 10 },
          { from: "step1_identify", to: "step2_calculate_apothem", type: "enables", strength: 8 },
          { from: "step2_calculate_apothem", to: "step3_calculate_perimeter", type: "enables", strength: 9 },
          { from: "step3_calculate_perimeter", to: "step4_apply_formula", type: "enables", strength: 9 },
          { from: "step4_apply_formula", to: "step5_calculate", type: "enables", strength: 10 },
          { from: "step5_calculate", to: "step6_write_answer", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'mensuration_hard_02',
      problem: "Find the volume of a frustum of a cone with radii 3 cm and 1 cm, and height 4 cm",
      problemType: "mensuration",
      difficulty: "harder",
      description: "Frustum volume calculation",
      graphData: {
        nodes: [
          {
            id: "radius1",
            name: "Top Radius",
            type: "concepts",
            description: "The top radius",
            value: "1 cm",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "radius2",
            name: "Bottom Radius",
            type: "concepts",
            description: "The bottom radius",
            value: "3 cm",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "height",
            name: "Height",
            type: "concepts",
            description: "The height of the frustum",
            value: "4 cm",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "frustum_volume_formula",
            name: "Frustum Volume Formula",
            type: "formulas",
            description: "Volume of frustum = ⅓πh(r₁² + r₂² + r₁r₂)",
            value: "Volume = ⅓πh(r₁² + r₂² + r₁r₂)",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "volume_formula",
            name: "Volume Formula",
            type: "formulas",
            description: "Volume = ⅓πh(r₁² + r₂² + r₁r₂)",
            value: "Volume = ⅓π × 4(1² + 3² + 1×3)",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "calculation",
            name: "Calculation",
            type: "values",
            description: "Calculate the volume",
            value: "Volume = ⅓π × 4(1 + 9 + 3) = 54.45",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The volume of the frustum",
            value: "54.45 cm³",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "step1_identify",
            name: "Step 1: Identify",
            type: "steps",
            description: "Recognize the given dimensions",
            value: "Step 1: r₁ = 1 cm, r₂ = 3 cm, h = 4 cm",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "step2_apply_formula",
            name: "Step 2: Apply Formula",
            type: "steps",
            description: "Use the frustum volume formula",
            value: "Step 2: Volume = ⅓π × 4(1² + 3² + 1×3)",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "step3_calculate",
            name: "Step 3: Calculate",
            type: "steps",
            description: "Perform the calculation",
            value: "Step 3: Volume = ⅓π × 4(1 + 9 + 3) = 54.45",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "step4_write_answer",
            name: "Step 4: Write Answer",
            type: "steps",
            description: "State the final answer with units",
            value: "Step 4: Volume = 54.45 cm³",
            importance: 10,
            x: 0, y: 150, z: 20
          }
        ],
        links: [
          { from: "radius1", to: "volume_formula", type: "used in", strength: 9 },
          { from: "radius2", to: "volume_formula", type: "used in", strength: 9 },
          { from: "height", to: "volume_formula", type: "used in", strength: 9 },
          { from: "volume_formula", to: "calculation", type: "gives", strength: 10 },
          { from: "calculation", to: "result", type: "equals", strength: 10 },
          { from: "frustum_volume_formula", to: "result", type: "gives", strength: 10 },
          { from: "step1_identify", to: "step2_apply_formula", type: "enables", strength: 8 },
          { from: "step2_apply_formula", to: "step3_calculate", type: "enables", strength: 9 },
          { from: "step3_calculate", to: "step4_write_answer", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'mensuration_hard_03',
      problem: "Find the surface area of a cone with radius 3 cm and slant height 5 cm",
      problemType: "mensuration",
      difficulty: "harder",
      description: "Cone surface area calculation",
      graphData: {
        nodes: [
          {
            id: "radius",
            name: "Radius",
            type: "concepts",
            description: "The radius of the cone",
            value: "3 cm",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "slant_height",
            name: "Slant Height",
            type: "concepts",
            description: "The slant height of the cone",
            value: "5 cm",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "lateral_surface_area",
            name: "Lateral Surface Area",
            type: "formulas",
            description: "LSA = πrl",
            value: "LSA = π × 3 × 5",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "base_area",
            name: "Base Area",
            type: "formulas",
            description: "Base Area = πr²",
            value: "Base Area = π × 3²",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "cone_surface_area_formula",
            name: "Cone Surface Area Formula",
            type: "formulas",
            description: "Total surface area of cone = LSA + Base Area",
            value: "TSA = πrl + πr²",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "total_surface_area",
            name: "Total Surface Area",
            type: "formulas",
            description: "TSA = LSA + Base Area",
            value: "TSA = 15π + 9π = 24π",
            importance: 10,
            x: 0, y: 100, z: -15
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The total surface area",
            value: "75.40 cm²",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "step1_identify",
            name: "Step 1: Identify",
            type: "steps",
            description: "Recognize the given dimensions",
            value: "Step 1: radius = 3 cm, slant height = 5 cm",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "step2_calculate_lsa",
            name: "Step 2: Calculate LSA",
            type: "steps",
            description: "Calculate lateral surface area",
            value: "Step 2: LSA = π × 3 × 5 = 15π",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "step3_calculate_base",
            name: "Step 3: Calculate Base Area",
            type: "steps",
            description: "Calculate base area",
            value: "Step 3: Base Area = π × 3² = 9π",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "step4_add_areas",
            name: "Step 4: Add Areas",
            type: "steps",
            description: "Add lateral and base areas",
            value: "Step 4: TSA = 15π + 9π = 24π",
            importance: 10,
            x: 0, y: 150, z: 20
          },
          {
            id: "step5_calculate",
            name: "Step 5: Calculate",
            type: "steps",
            description: "Perform the final calculation",
            value: "Step 5: TSA = 24π = 75.40",
            importance: 10,
            x: 0, y: 200, z: 20
          },
          {
            id: "step6_write_answer",
            name: "Step 6: Write Answer",
            type: "steps",
            description: "State the final answer with units",
            value: "Step 6: Surface Area = 75.40 cm²",
            importance: 10,
            x: 0, y: 250, z: 20
          }
        ],
        links: [
          { from: "radius", to: "lateral_surface_area", type: "used in", strength: 9 },
          { from: "slant_height", to: "lateral_surface_area", type: "used in", strength: 9 },
          { from: "radius", to: "base_area", type: "used in", strength: 9 },
          { from: "lateral_surface_area", to: "total_surface_area", type: "used in", strength: 9 },
          { from: "base_area", to: "total_surface_area", type: "used in", strength: 9 },
          { from: "total_surface_area", to: "result", type: "equals", strength: 10 },
          { from: "cone_surface_area_formula", to: "result", type: "gives", strength: 10 },
          { from: "step1_identify", to: "step2_calculate_lsa", type: "enables", strength: 8 },
          { from: "step2_calculate_lsa", to: "step3_calculate_base", type: "enables", strength: 9 },
          { from: "step3_calculate_base", to: "step4_add_areas", type: "enables", strength: 9 },
          { from: "step4_add_areas", to: "step5_calculate", type: "enables", strength: 10 },
          { from: "step5_calculate", to: "step6_write_answer", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'mensuration_hard_04',
      problem: "Find the area of an ellipse with semi-major axis 6 cm and semi-minor axis 4 cm",
      problemType: "mensuration",
      difficulty: "harder",
      description: "Ellipse area calculation",
      graphData: {
        nodes: [
          {
            id: "semi_major_axis",
            name: "Semi-Major Axis",
            type: "concepts",
            description: "The semi-major axis",
            value: "6 cm",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "semi_minor_axis",
            name: "Semi-Minor Axis",
            type: "concepts",
            description: "The semi-minor axis",
            value: "4 cm",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "ellipse_area_formula",
            name: "Ellipse Area Formula",
            type: "formulas",
            description: "Area of ellipse = πab",
            value: "Area = πab",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "area_formula",
            name: "Area Formula",
            type: "formulas",
            description: "Area = πab",
            value: "Area = π × 6 × 4",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "calculation",
            name: "Calculation",
            type: "values",
            description: "Calculate the area",
            value: "Area = π × 24 = 75.40",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The area of the ellipse",
            value: "75.40 cm²",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "step1_identify",
            name: "Step 1: Identify",
            type: "steps",
            description: "Recognize the given axes",
            value: "Step 1: a = 6 cm, b = 4 cm",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "step2_apply_formula",
            name: "Step 2: Apply Formula",
            type: "steps",
            description: "Use the ellipse area formula",
            value: "Step 2: Area = π × 6 × 4",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "step3_calculate",
            name: "Step 3: Calculate",
            type: "steps",
            description: "Perform the calculation",
            value: "Step 3: Area = π × 24 = 75.40",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "step4_write_answer",
            name: "Step 4: Write Answer",
            type: "steps",
            description: "State the final answer with units",
            value: "Step 4: Area = 75.40 cm²",
            importance: 10,
            x: 0, y: 150, z: 20
          }
        ],
        links: [
          { from: "semi_major_axis", to: "area_formula", type: "used in", strength: 9 },
          { from: "semi_minor_axis", to: "area_formula", type: "used in", strength: 9 },
          { from: "area_formula", to: "calculation", type: "gives", strength: 10 },
          { from: "calculation", to: "result", type: "equals", strength: 10 },
          { from: "ellipse_area_formula", to: "result", type: "gives", strength: 10 },
          { from: "step1_identify", to: "step2_apply_formula", type: "enables", strength: 8 },
          { from: "step2_apply_formula", to: "step3_calculate", type: "enables", strength: 9 },
          { from: "step3_calculate", to: "step4_write_answer", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'mensuration_hard_05',
      problem: "Find the volume of a pyramid with square base of side 4 cm and height 6 cm",
      problemType: "mensuration",
      difficulty: "harder",
      description: "Pyramid volume calculation",
      graphData: {
        nodes: [
          {
            id: "base_side",
            name: "Base Side",
            type: "concepts",
            description: "The side of the square base",
            value: "4 cm",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "height",
            name: "Height",
            type: "concepts",
            description: "The height of the pyramid",
            value: "6 cm",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "base_area",
            name: "Base Area",
            type: "values",
            description: "Calculate base area",
            value: "Base Area = 4² = 16 cm²",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "pyramid_volume_formula",
            name: "Pyramid Volume Formula",
            type: "formulas",
            description: "Volume of pyramid = ⅓ × base area × height",
            value: "Volume = ⅓ × base area × height",
            importance: 10,
            x: 0, y: 50, z: 40
          },
          {
            id: "volume_formula",
            name: "Volume Formula",
            type: "formulas",
            description: "Volume = ⅓ × base area × height",
            value: "Volume = ⅓ × 16 × 6",
            importance: 10,
            x: 0, y: 0, z: 25
          },
          {
            id: "calculation",
            name: "Calculation",
            type: "values",
            description: "Calculate the volume",
            value: "Volume = 32",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "result",
            name: "Result",
            type: "values",
            description: "The volume of the pyramid",
            value: "32 cm³",
            importance: 10,
            x: 0, y: 200, z: 0
          },
          {
            id: "step1_identify",
            name: "Step 1: Identify",
            type: "steps",
            description: "Recognize the given dimensions",
            value: "Step 1: base side = 4 cm, height = 6 cm",
            importance: 8,
            x: -250, y: -50, z: 0
          },
          {
            id: "step2_calculate_base",
            name: "Step 2: Calculate Base Area",
            type: "steps",
            description: "Calculate the base area",
            value: "Step 2: Base Area = 4² = 16 cm²",
            importance: 9,
            x: 250, y: -50, z: 0
          },
          {
            id: "step3_apply_formula",
            name: "Step 3: Apply Formula",
            type: "steps",
            description: "Use the pyramid volume formula",
            value: "Step 3: Volume = ⅓ × 16 × 6",
            importance: 10,
            x: 0, y: 100, z: 20
          },
          {
            id: "step4_calculate",
            name: "Step 4: Calculate",
            type: "steps",
            description: "Perform the calculation",
            value: "Step 4: Volume = 32",
            importance: 10,
            x: 0, y: 150, z: 20
          },
          {
            id: "step5_write_answer",
            name: "Step 5: Write Answer",
            type: "steps",
            description: "State the final answer with units",
            value: "Step 5: Volume = 32 cm³",
            importance: 10,
            x: 0, y: 200, z: 20
          }
        ],
        links: [
          { from: "base_side", to: "base_area", type: "used to calculate", strength: 9 },
          { from: "base_area", to: "volume_formula", type: "used in", strength: 9 },
          { from: "height", to: "volume_formula", type: "used in", strength: 9 },
          { from: "volume_formula", to: "calculation", type: "gives", strength: 10 },
          { from: "calculation", to: "result", type: "equals", strength: 10 },
          { from: "pyramid_volume_formula", to: "result", type: "gives", strength: 10 },
          { from: "step1_identify", to: "step2_calculate_base", type: "enables", strength: 8 },
          { from: "step2_calculate_base", to: "step3_apply_formula", type: "enables", strength: 9 },
          { from: "step3_apply_formula", to: "step4_calculate", type: "enables", strength: 10 },
          { from: "step4_calculate", to: "step5_write_answer", type: "equals", strength: 10 }
        ]
      }
    }
  ]
}; 